'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { CartCross, CrossDark } from '../../assets/icons';
import { RouteIntlScope } from 'lib/i18n/routeIntl';
import { pauseHomepageLenis, resumeHomepageLenis } from 'utils/homepageLenis';
import { useFocusTrap } from 'hooks/dom/useFocusTrap';
import styles from './Modal.module.css';

interface ModalContextType {
  set: (content: React.ReactNode) => void;
  clear: () => void;
  show: () => void;
  hide: () => void;
  setCloseButtonTheme: (theme: 'light' | 'dark') => void;
  setCloseBtnHidden: (hidden: boolean) => void;
  setTop: () => void;
  setAlignRight: () => void;
  setFullView: () => void;
  setBg: (col: string) => void;
  setPortal: (portal: boolean) => void;
  isPortal: boolean;
}

const initialModalContext: ModalContextType = {
  set: () => {},
  clear: () => {},
  show: () => {},
  hide: () => {},
  setCloseButtonTheme: () => {},
  setCloseBtnHidden: () => {},
  setTop: () => {},
  setAlignRight: () => {},
  setFullView: () => {},
  setBg: () => {},
  setPortal: () => {},
  isPortal: false,
};

export const ModalContext =
  createContext<ModalContextType>(initialModalContext);

export function useModal(): ModalContextType {
  return useContext(ModalContext);
}

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode | null>(
    null,
  );
  const [background, setBackground] = useState<string>('');
  const [lightCloseButton, setLightCloseButton] = useState(false);

  const [topMost, setTopMost] = useState(false);

  const [fullViewOnMbl, setFullViewOnMbl] = useState(false);

  const [closeBtnHidden, setCloseBtnHidden] = useState<boolean>(false);

  const [isPortal, setIsPortal] = useState<boolean>(false);
  const [alignRight, setAlignRightState] = useState<boolean>(false);
  const set = useCallback((content: React.ReactNode) => {
    setModalContent(content ?? null);
  }, []);
  const hide = useCallback(() => {
    setModalVisible(false);
    setLightCloseButton(false);
    setFullViewOnMbl(false);
    setCloseBtnHidden(false);
    setAlignRightState(false);
  }, []);
  const clear = useCallback(() => {
    setModalContent(null);
    hide();
  }, [hide]);

  const setBg = useCallback((col: string) => {
    setBackground(col);
  }, []);
  const show = useCallback(() => {
    setModalVisible(true);
  }, []);
  const setCloseButtonTheme = useCallback((theme: 'light' | 'dark') => {
    if (theme == 'dark') return;
    setLightCloseButton(true);
  }, []);

  const setTop = useCallback(() => setTopMost(true), []);

  const setFullView = useCallback(() => setFullViewOnMbl(true), []);

  const setAlignRight = useCallback(() => setAlignRightState(true), []);
  const setPortal = useCallback((portal: boolean) => setIsPortal(portal), []);

  useEffect(() => {
    if (!isModalVisible) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    pauseHomepageLenis();
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
      resumeHomepageLenis();
    };
  }, [isModalVisible]);

  const contextValue = useMemo(
    () => ({
      set,
      clear,
      show,
      hide,
      setCloseButtonTheme,
      setCloseBtnHidden,
      setTop,
      setFullView,
      setBg,
      setAlignRight,
      setPortal,
      isPortal,
    }),
    [
      set,
      clear,
      show,
      hide,
      setCloseButtonTheme,
      setCloseBtnHidden,
      setTop,
      setFullView,
      setBg,
      setAlignRight,
      setPortal,
      isPortal,
    ],
  );

  const modalElement = isModalVisible ? (
    <ModalWhite
      top={topMost || isPortal}
      lightCloseButton={lightCloseButton}
      hideModal={hide}
      fullViewMbl={fullViewOnMbl}
      bg={background}
      closeBtnHidden={closeBtnHidden}
      alignRight={alignRight}
    >
      <RouteIntlScope>{modalContent}</RouteIntlScope>
    </ModalWhite>
  ) : null;

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {isPortal && typeof document !== 'undefined'
        ? createPortal(modalElement, document.body)
        : modalElement}
    </ModalContext.Provider>
  );
};

const ModalWhite = ({
  className,
  children,
  hideModal,
  lightCloseButton,
  top,
  fullViewMbl,
  bg,
  closeBtnHidden = false,
  alignRight = false,
}: {
  className?: string;
  children?: React.ReactNode;
  hideModal?: () => void;
  lightCloseButton?: boolean;
  top: boolean;
  fullViewMbl?: boolean;
  bg?: string;
  closeBtnHidden?: boolean;
  alignRight?: boolean;
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('common');

  useFocusTrap(rootRef, {
    onEscape: hideModal,
    initialFocus: (root) =>
      root.querySelector<HTMLElement>('[data-modal-close]'),
  });

  return (
    <div
      ref={rootRef}
      className={`${styles.root} ${className ?? ''} ${top ? styles.top : ''} ${
        fullViewMbl ? styles.fullViewBackdrop : ''
      } ${alignRight ? styles.alignRight : ''}`}
      onClick={hideModal}
      // A stopped smooth-scroller preventDefaults every wheel event unless the
      // event path opts out; the backdrop's own scroll must still work.
      data-lenis-prevent="true"
    >
      <div
        role="dialog"
        aria-modal="true"
        className={`modalContent ${styles.modalContent} ${
          bg === 'dark' ? styles.darkBg : ''
        } ${fullViewMbl ? styles.fullViewMobile : ''} ${
          alignRight ? styles.alignRightContent : ''
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
        {fullViewMbl || closeBtnHidden ? null : (
          <button
            className={`${styles.closeBtn} ${
              lightCloseButton ? styles.light : styles.filled
            }`}
            onClick={hideModal}
            type="button"
            aria-label={t('modal.button.ariaLabel.close')}
            data-modal-close
          >
            {lightCloseButton ? <CrossDark /> : <CartCross />}
          </button>
        )}
        {/* <div className="gradient-container" /> */}
      </div>
      {fullViewMbl && !closeBtnHidden ? (
        <button
          className={`${styles.closeNBtn} ${lightCloseButton ? styles.light : ''}`}
          onClick={hideModal}
          type="button"
          aria-label={t('modal.button.ariaLabel.close')}
          data-modal-close
        >
          <CrossDark />
        </button>
      ) : null}
    </div>
  );
};
